import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, HelpCircle, Search, Book, MessageCircle, Star, Zap } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";


interface HelpCenterProps {
  onBack: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onCreateNew: () => void;
  user?: {
    email?: string;
    name?: string;
  };
}

export const HelpCenter = ({
  onBack,
  onNavigateToChat,
  onNavigateToMyBoyfriends,
  onNavigateToProfile,
  onNavigateToSettings,
  onCreateNew,
  user
}: HelpCenterProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const helpCategories = [
    {
      title: t('help_center.category.getting_started'),
      icon: Star,
      color: "text-blue-600",
      articles: [
        { title: t('help_center.article.create_first_boyfriend') },
        { title: t('help_center.article.personality_types') },
        { title: t('help_center.article.customize_appearance') },
        { title: t('help_center.article.first_conversation') },
      ]
    },
    {
      title: t('help_center.category.premium_features'),
      icon: Zap,
      color: "text-primary",
      articles: [
        { title: t('help_center.article.premium_included') },
        { title: t('help_center.article.upgrade_premium') },
        { title: t('help_center.article.manage_subscription') },
        { title: t('help_center.article.premium_characters') },
      ]
    },
    {
      title: t('help_center.category.conversations'),
      icon: MessageCircle,
      color: "text-green-600",
      articles: [
        { title: t('help_center.article.ai_conversations') },
        { title: t('help_center.article.improve_quality') },
        { title: t('help_center.article.voice_messages') },
        { title: t('help_center.article.sharing_media') },
      ]
    },
    {
      title: t('help_center.category.account_privacy'),
      icon: Book,
      color: "text-purple-600",
      articles: [
        { title: t('help_center.article.privacy_data') },
        { title: t('help_center.article.manage_account') },
        { title: t('help_center.article.delete_conversations') },
        { title: t('help_center.article.export_data') },
      ]
    }
  ];

  const filteredCategories = helpCategories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Main Content */}
      <div className="p-4 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-primary/20" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
            <Button variant="ghost" size="icon" onClick={onNavigateToProfile}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              {t('help_center.title')}
            </h1>
          </div>

          {/* Search */}
          <Card className="p-6 mb-6 border-primary/20">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={t('help_center.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </Card>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card className="p-4 border-primary/20 hover:border-primary/40 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <Book className="h-8 w-8 text-blue-600" />
                <div>
                  <h3 className="font-semibold">{t('help_center.user_guide')}</h3>
                  <p className="text-sm text-muted-foreground">{t('help_center.user_guide_desc')}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-primary/20 hover:border-primary/40 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <Star className="h-8 w-8 text-yellow-600" />
                <div>
                  <h3 className="font-semibold">{t('help_center.feature_requests')}</h3>
                  <p className="text-sm text-muted-foreground">{t('help_center.feature_requests_desc')}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Help Categories */}
          <div className="space-y-6">
            {(searchQuery ? filteredCategories : helpCategories).map((category) => (
              <Card key={category.title} className="p-6 border-primary/20">
                <div className="flex items-center gap-2 mb-4">
                  <category.icon className={`h-5 w-5 ${category.color}`} />
                  <h3 className="font-semibold">{category.title}</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {category.articles.map((article) => (
                    <div
                      key={article.title}
                      className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors cursor-pointer group"
                    >
                      <div className="flex-1">
                        <h4 className="font-medium group-hover:text-primary transition-colors">
                          {article.title}
                        </h4>
                      </div>
                      <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                        →
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          {/* No Results */}
          {searchQuery && filteredCategories.length === 0 && (
            <Card className="p-8 text-center border-primary/20">
              <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">{t('help_center.no_articles_found')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('help_center.no_articles_found_desc', { query: searchQuery })}
              </p>
              <Button variant="romantic" onClick={() => setSearchQuery("")}>
                {t('help_center.view_all_articles')}
              </Button>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
};
